import './index.scss';
import { ID } from '@phi/newtype';
import { defineComponent, h, PropType } from 'vue';
import * as API from '@phi/frontend/api/calls';
import SearchSelect from '@frontend/components/SearchSelect';
import { Option } from '@vaaas/fpts/option';
import { Record } from '@bloomberg/record-tuple-polyfill';
import { PaginatedOnshapeDocuments } from '@features/list-onshape-documents';
import { debounce } from '@vaaas/fpts/timing';

export type DocumentWorkspace = {
    document: Option<ID<'document'>>;
    workspace: Option<ID<'workspace'>>;
}
const SearchDocument = SearchSelect<DocumentWorkspace>();

const isNewDocument = (x: DocumentWorkspace): x is { document: undefined; workspace: undefined } => (x.document === undefined && x.workspace === undefined);

export default defineComponent({
    props: {
        value: {
            required: true,
            type: undefined as any as PropType<DocumentWorkspace>,
        },

        api: {
            required: false,
            type: Object as PropType<typeof API>,
            default: () => API,
        },

        onshape_documents_store: {
            required: false,
            type: Object as PropType<PaginatedOnshapeDocuments>,
            default: () => new PaginatedOnshapeDocuments(),
        },
    },

    emits: {
        'update:value': (x: DocumentWorkspace) => true,
    },

    data() {
        return { searchText: '' };
    },

    async mounted() {
        if (!this.onshape_documents_store.has_loaded())
            this.onshape_documents_store.more();
    },

    methods: {
        async maybe_fetch_more() {
            this.onshape_documents_store.more();
        },

        update_search_text(x: string) {
            this.searchText = x;
            if (this.searchText.length)
                this.onshape_documents_store.search(this.searchText);
        },
    },

    computed: {
        items(): DocumentWorkspace[] {
            return [
                Record({ document: undefined, workspace: undefined }),
                ...this.onshape_documents_store.items.map(x => Record({
                    document: x.id,
                    workspace: x.defaultWorkspace.id,
                }))
            ].filter(x => {
                if (isNewDocument(x)) return true;
                else return this.onshape_documents_store.items
                    .find(y => y.id === x.document)!
                    .name
                    .toLowerCase()
                    .includes(this.searchText.toLowerCase());
            });
        },
    },

    render() {
        return h(SearchDocument, {
            class: 'simple-select-document-element',
            search: this.searchText,
            value: this.value,
            items: this.items,
            selected: this.value,
            'onUpdate:search': debounce(this.update_search_text, 500),
            'onUpdate:selected': x => this.$emit('update:value', x),
            'onScrolledToLast': this.maybe_fetch_more,
            placeholder: 'Search Onshape Documents',
            label: 'Select Onshape Document',
        }, {
            item: (x: DocumentWorkspace) => {
                if (isNewDocument(x))
                    return h('span', { class: 'new-document' }, 'Create a new onshape document');
                else
                    return h('span', this.onshape_documents_store.items.find(y => y.id === x.document)!.name);
            }
        });
    }
});
